import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DataTable from '../../../sharedModules/dataTable/DataTable/DataTable';
import { IDataTableColumn } from '../../../sharedModules/dataTable/DataTable/model';

interface LineRow {
  id: number;
  expected: string;
  ordered: number;
  description: string;
}

interface POSummary {
  poNumber: string;
  vendorId: number;
}

const MOCK_PO_SUMMARY: Record<number, POSummary> = {
  101: { poNumber: 'PO-2026-0441', vendorId: 1 },
  102: { poNumber: 'PO-2026-0442', vendorId: 1 },
  103: { poNumber: 'PO-2026-0443', vendorId: 1 },
  201: { poNumber: 'PO-2026-0550', vendorId: 2 },
  202: { poNumber: 'PO-2026-0551', vendorId: 2 },
  301: { poNumber: 'PO-2026-0610', vendorId: 3 },
  401: { poNumber: 'PO-2026-0720', vendorId: 4 },
  402: { poNumber: 'PO-2026-0721', vendorId: 4 },
  501: { poNumber: 'PO-2026-0800', vendorId: 5 },
  601: { poNumber: 'PO-2026-0901', vendorId: 6 },
};

const MOCK_LINES: Record<number, LineRow[]> = {
  101: [
    { id: 1, expected: '03/18/2026', ordered: 2, description: 'Condenser Coil Assembly / AAON Part #C0-30000' },
    { id: 2, expected: '03/18/2026', ordered: 2, description: 'Evaporator Fan Motor 5HP / AAON Part #M0-10050' },
  ],
  102: [
    { id: 1, expected: '03/19/2026', ordered: 1, description: 'Variable Frequency Drive 10HP / AAON Part #V0-21100' },
    { id: 2, expected: '03/19/2026', ordered: 1, description: 'Control Board Assembly / AAON Part #CB-00310' },
  ],
  103: [
    { id: 1, expected: '03/21/2026', ordered: 4, description: 'Compressor Scroll 3-Ton / AAON Part #CP-30030' },
    { id: 2, expected: '03/21/2026', ordered: 2, description: 'Refrigerant Valve TXV / AAON Part #RV-00120' },
    { id: 3, expected: '03/21/2026', ordered: 2, description: 'Filter Drier Assembly / AAON Part #FD-00050' },
  ],
  201: [
    {
      id: 1,
      expected: '03/19/2026',
      ordered: 6,
      description: 'Split System Indoor Coil 24k BTU / Carrier Part #FB4CNP024000',
    },
  ],
  202: [
    { id: 1, expected: '03/20/2026', ordered: 1, description: 'Communicating Thermostat / Carrier Part #TP-WEM01-A' },
  ],
  301: [{ id: 1, expected: '03/20/2026', ordered: 3, description: 'Chiller Module Board / Trane Part #CNT05228' }],
  401: [{ id: 1, expected: '03/17/2026', ordered: 2, description: 'Bearing Kit 2-Stage / Daikin Part #BK-4400-A' }],
  402: [
    { id: 1, expected: '03/18/2026', ordered: 3, description: 'Scroll Compressor ZP61KCE / Daikin Part #SC-ZP61' },
    { id: 2, expected: '03/18/2026', ordered: 2, description: 'Capacitor 45/5 MFD / Daikin Part #CAP-4505' },
  ],
  501: [
    { id: 1, expected: '03/21/2026', ordered: 4, description: 'Furnace Heat Exchanger / York Part #S1-02625020000' },
  ],
  601: [
    { id: 1, expected: '03/22/2026', ordered: 5, description: 'Air Handler Cabinet Panel Kit / Lennox Part #67M15' },
    { id: 2, expected: '03/22/2026', ordered: 3, description: 'Blower Wheel 10x9 / Lennox Part #53J18' },
    { id: 3, expected: '03/22/2026', ordered: 2, description: 'Contactor 2P 40A / Lennox Part #18H16' },
  ],
};

const WarehousePickupPODetailsPage: React.FC = () => {
  const { poId } = useParams<{ poId: string }>();
  const history = useHistory();

  const numericPoId = parseInt(poId, 10);
  const poSummary = MOCK_PO_SUMMARY[numericPoId];
  const lines = MOCK_LINES[numericPoId] || [];

  const columns: IDataTableColumn<LineRow>[] = [
    {
      name: 'Expected',
      selector: 'expected',
      sortable: true,
      grow: 0.7,
    },
    {
      name: 'Ordered Qty',
      selector: 'ordered',
      sortable: true,
      grow: 0.5,
    },
    {
      name: 'Description',
      selector: 'description',
      sortable: false,
      grow: 3,
      wrap: true,
    },
  ];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Button
          variant="text"
          onClick={() =>
            poSummary
              ? history.push(`/employee_portal/warehouse_pickup/vendors/${poSummary.vendorId}/pos`)
              : history.goBack()
          }
          sx={{ mb: 1, pl: 0 }}
        >
          ← Back to Vendor POs
        </Button>
        <Typography variant="h5" fontWeight="bold">
          PO Details — {poSummary?.poNumber ?? `PO #${poId}`}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Line items for this purchase order. Read-only view.
        </Typography>
        <Tooltip title="Receiving not yet available in this module">
          <span>
            <Button variant="contained" color="error" disabled sx={{ mb: 2 }}>
              Receive PO {poSummary?.poNumber ?? poId}
            </Button>
          </span>
        </Tooltip>
      </Grid>
      <Grid item xs={12}>
        <DataTable columns={columns} data={lines} striped dense={false} noHeader responsive />
      </Grid>
    </Grid>
  );
};

export default WarehousePickupPODetailsPage;
